import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Search } from "lucide-react";
import { ThemeToggle } from "../components/layout/ThemeToggle";
import { VersionBanner } from "../components/layout/VersionBanner";
import { SecurityButton } from "../components/security/SecurityButton";
import { SecurityNotices } from "../components/security/SecurityNotices";
import { Button } from "../components/ui/button";
import { ProjectCard } from "../components/projects/ProjectCard";
import { NewProjectDialog } from "../components/projects/NewProjectDialog";
import { GeneralChatBox } from "../components/projects/GeneralChatBox";
import { CATEGORIES, NO_CATEGORY } from "../components/projects/categories";
import { TallerDialog } from "../components/projects/TallerDialog";
import { PanelSecretsDialog } from "../components/projects/PanelSecretsDialog";
import { ServersDialog } from "../components/servers/ServersDialog";
import { DeleteProjectDialog } from "../components/projects/DeleteProjectDialog";
import { useAuth } from "../hooks/useAuth";
import { useGeneral, useProjects, useUpdateProject } from "../hooks/useProjects";
import { useVersion } from "../hooks/useVersion";

export default function HomePage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: allProjects, isLoading } = useProjects();
  const projects = useMemo(() => (allProjects || []).filter((p) => p.kind !== "general"), [allProjects]); // el agente general no es una tarjeta: tiene su ventana arriba
  const { data: general } = useGeneral();
  const updateProject = useUpdateProject();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(() => {
    try {
      return window.localStorage.getItem("projects-filter") || "Todos";
    } catch {
      return "Todos";
    }
  });
  const chooseFilter = (value) => {
    setFilter(value);
    try {
      window.localStorage.setItem("projects-filter", value);
    } catch {
      // sin almacenamiento: el filtro solo dura mientras la página está abierta
    }
  };
  const categoryOf = (p) => p.category || NO_CATEGORY;
  const counts = {};
  projects.forEach((p) => { counts[categoryOf(p)] = (counts[categoryOf(p)] || 0) + 1; });
  const chips = ["Todos", ...CATEGORIES.filter((c) => counts[c]), ...(counts[NO_CATEGORY] ? [NO_CATEGORY] : [])];
  const activeFilter = chips.includes(filter) ? filter : "Todos";
  const needle = search.trim().toLowerCase();
  const visible = projects.filter(
    (p) => (activeFilter === "Todos" || categoryOf(p) === activeFilter) && (!needle || `${p.name} ${p.description || ""} ${categoryOf(p)}`.toLowerCase().includes(needle))
  );
  const { hasNewVersion, reload } = useVersion();
  const [showNewProject, setShowNewProject] = useState(false);
  const [showTaller, setShowTaller] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [serversProjectId, setServersProjectId] = useState(null);
  const serversProject = projects?.find((p) => p.id === serversProjectId);
  const [deleteTarget, setDeleteTarget] = useState(null); // objeto completo: el proyecto desaparece de la lista al borrarse

  const handleOpenProject = (id) => navigate(`/chat/${id}`);
  // Escribir en la ventanita abre el chat completo del agente general (en su chat principal) con ese mensaje ya enviado.
  const handleGeneralSend = (text) => navigate("/chat/general", { state: { autoMessage: text, conversationId: general?.main_conversation_id || null } });
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div data-testid="home-page" className="flex flex-col h-screen bg-background text-foreground">
      <VersionBanner visible={hasNewVersion} onReload={reload} />
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4">
        <span className="font-mono text-sm font-semibold tracking-tight">Panel Orquestador</span>
        <div className="flex items-center gap-1">
          <SecurityButton />
          <ThemeToggle />
          <Button data-testid="logout-button" variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <SecurityNotices />

      <main className="flex-1 overflow-y-auto">
        <section
          data-testid="general-zone"
          className="border-b border-border/60 px-6 pb-10 pt-10"
          style={{ backgroundImage: "radial-gradient(70% 90% at 50% 0%, rgba(16,185,129,0.13), transparent 72%)" }}
        >
          <GeneralChatBox general={general} onSend={handleGeneralSend} onContinue={() => navigate("/chat/general")} />
        </section>
        <section data-testid="projects-zone" className="min-h-[50vh] bg-muted/20 px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-semibold">Tus proyectos</h1>
              <p className="text-sm text-muted-foreground">
                Selecciona un proyecto para entrar a su chat, o da de alta uno nuevo.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="secrets-button" variant="outline" onClick={() => setShowSecrets(true)}>
                Claves
              </Button>
              <Button data-testid="taller-button" variant="outline" onClick={() => setShowTaller(true)}>
                Taller
              </Button>
              <Button data-testid="new-project-button" onClick={() => setShowNewProject(true)}>
                <Plus className="h-4 w-4 mr-1.5" /> Nuevo proyecto
              </Button>
            </div>
          </div>

          {projects.length > 0 && (
            <div className="mb-5 space-y-3">
              <div className="relative max-w-sm">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="project-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar un proyecto…"
                  aria-label="Buscar un proyecto"
                  className="w-full h-9 rounded-md border border-input bg-background pl-8 pr-3 text-sm"
                />
              </div>
              <div data-testid="project-filters" className="flex flex-wrap gap-1.5">
                {chips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    data-testid={`project-filter-${chip}`}
                    aria-pressed={chip === activeFilter}
                    onClick={() => chooseFilter(chip)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      chip === activeFilter ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {chip} ({chip === "Todos" ? projects.length : counts[chip]})
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLoading && <p className="text-sm text-muted-foreground">Cargando proyectos...</p>}

          {!isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visible.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onOpen={handleOpenProject}
                  onManageServers={(p) => setServersProjectId(p.id)}
                  onDelete={setDeleteTarget}
                  onChangeCategory={(p, category) => updateProject.mutate({ id: p.id, category })}
                />
              ))}
            </div>
          )}

          {!isLoading && projects.length > 0 && visible.length === 0 && (
            <p data-testid="projects-no-match" className="text-sm text-muted-foreground">Ningún proyecto coincide con tu búsqueda o filtro.</p>
          )}

          {!isLoading && projects.length === 0 && (
            <p data-testid="projects-empty" className="text-sm text-muted-foreground">
              Todavía no tienes proyectos. Crea el primero con "Nuevo proyecto".
            </p>
          )}
        </div>
        </section>
      </main>

      <NewProjectDialog
        open={showNewProject}
        onOpenChange={setShowNewProject}
        onProjectCreated={(id) => navigate(`/chat/${id}`)}
      />
      <TallerDialog open={showTaller} onOpenChange={setShowTaller} />
      <PanelSecretsDialog open={showSecrets} onOpenChange={setShowSecrets} />
      <ServersDialog
        open={!!serversProject}
        onOpenChange={(next) => !next && setServersProjectId(null)}
        project={serversProject}
      />
      <DeleteProjectDialog
        open={!!deleteTarget}
        onOpenChange={(next) => !next && setDeleteTarget(null)}
        project={deleteTarget}
      />
    </div>
  );
}
